/**
 * COOLFLY Download / Hub Page
 * - iOS → App Store, Android → Google Play, Desktop → store chooser
 * - Banner auto slideshow
 * - Appends source / product / channel without overwriting existing params
 */
(function () {
  'use strict';

  var TRACKING_KEYS = ['source', 'product', 'channel'];

  function getTrackingParams() {
    var params = {};
    var search = new URLSearchParams(window.location.search);

    TRACKING_KEYS.forEach(function (key) {
      var value = search.get(key);
      if (value) {
        params[key] = value.trim();
      }
    });

    return params;
  }

  function appendParams(url, params) {
    if (!url || url.indexOf('REPLACE_WITH_') === 0) {
      return url;
    }

    try {
      var parsed = new URL(url, window.location.origin);
      Object.keys(params).forEach(function (key) {
        if (params[key] && !parsed.searchParams.has(key)) {
          parsed.searchParams.set(key, params[key]);
        }
      });
      return parsed.toString();
    } catch (err) {
      return url;
    }
  }

  function detectPlatform() {
    var ua = navigator.userAgent || navigator.vendor || window.opera || '';
    var platform = navigator.platform || '';
    var maxTouchPoints = navigator.maxTouchPoints || 0;

    var isIPadOS = /Macintosh/.test(ua) && maxTouchPoints > 1;
    var isIOS =
      /iPhone|iPad|iPod/i.test(ua) ||
      isIPadOS ||
      (/iPad|iPhone|iPod/.test(platform) && !window.MSStream);
    var isAndroid = /Android/i.test(ua);

    if (isIOS) return 'ios';
    if (isAndroid) return 'android';
    return 'desktop';
  }

  function isPlaceholder(url) {
    return !url || url.indexOf('REPLACE_WITH_') === 0;
  }

  function openUrl(url) {
    if (isPlaceholder(url)) {
      console.warn('[COOLFLY Download App] Replace placeholder URL before use:', url);
      return;
    }
    window.location.href = url;
  }

  function wireLinks(root, tracking) {
    var links = root.querySelectorAll('[data-dap-link]');

    links.forEach(function (link) {
      if (link.getAttribute('data-dap-link-wired') === '1') return;
      link.setAttribute('data-dap-link-wired', '1');

      var href = link.getAttribute('href');
      if (!href) return;

      var next = appendParams(href, tracking);
      if (next && next !== href) {
        link.setAttribute('href', next);
      }

      if (isPlaceholder(href)) {
        link.setAttribute('aria-disabled', 'true');
        link.addEventListener('click', function (event) {
          event.preventDefault();
          console.warn(
            '[COOLFLY Download App] Replace placeholder URL for:',
            link.getAttribute('data-dap-link'),
            href
          );
        });
      }
    });
  }

  function getStoreUrl(root, store, tracking) {
    var link = root.querySelector('[data-dap-store="' + store + '"]');
    var href = link ? link.getAttribute('href') : '';
    return appendParams(href, tracking);
  }

  function setChooserOpen(root, downloadBtn, chooser, open) {
    if (!chooser || !downloadBtn) return;
    if (open) {
      chooser.hidden = false;
      downloadBtn.setAttribute('aria-expanded', 'true');
    } else {
      chooser.hidden = true;
      downloadBtn.setAttribute('aria-expanded', 'false');
    }
  }

  function wireDownload(root, platform, tracking) {
    var downloadBtn = root.querySelector('[data-dap-download]');
    var chooser = root.querySelector('[data-dap-store-chooser]');
    if (!downloadBtn) return;
    if (downloadBtn.getAttribute('data-dap-download-wired') === '1') return;
    downloadBtn.setAttribute('data-dap-download-wired', '1');

    root.setAttribute('data-platform', platform);

    if (platform !== 'desktop') {
      setChooserOpen(root, downloadBtn, chooser, false);
    }

    downloadBtn.addEventListener('click', function () {
      if (platform === 'ios') {
        openUrl(getStoreUrl(root, 'ios', tracking));
        return;
      }

      if (platform === 'android') {
        openUrl(getStoreUrl(root, 'android', tracking));
        return;
      }

      if (!chooser) return;
      var willOpen = chooser.hidden;
      setChooserOpen(root, downloadBtn, chooser, willOpen);
    });
  }

  function initBanner(banner) {
    if (banner.getAttribute('data-dap-banner-wired') === '1') return;
    banner.setAttribute('data-dap-banner-wired', '1');

    var slides = Array.prototype.slice.call(banner.querySelectorAll('[data-dap-banner-slide]'));
    if (slides.length < 2) return;

    var dotsWrap = banner.querySelector('[data-dap-banner-dots]');
    var autoplay = banner.getAttribute('data-autoplay') !== 'false';
    var interval = parseInt(banner.getAttribute('data-interval'), 10) || 4000;
    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var index = 0;
    var timer = null;
    var dots = [];

    function goTo(next) {
      slides[index].classList.remove('is-active');
      slides[index].setAttribute('aria-hidden', 'true');
      if (dots[index]) dots[index].classList.remove('is-active');

      index = (next + slides.length) % slides.length;

      slides[index].classList.add('is-active');
      slides[index].setAttribute('aria-hidden', 'false');
      if (dots[index]) dots[index].classList.add('is-active');
    }

    function start() {
      stop();
      if (!autoplay || reduceMotion) return;
      timer = window.setInterval(function () {
        goTo(index + 1);
      }, interval);
    }

    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      slides.forEach(function (_, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dap__banner-dot' + (i === 0 ? ' is-active' : '');
        btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        btn.addEventListener('click', function () {
          goTo(i);
          start();
        });
        dotsWrap.appendChild(btn);
        dots.push(btn);
      });
    }

    banner.addEventListener('mouseenter', stop);
    banner.addEventListener('mouseleave', start);
    banner.addEventListener('focusin', stop);
    banner.addEventListener('focusout', start);

    banner.addEventListener(
      'touchstart',
      function () {
        stop();
      },
      { passive: true }
    );
    banner.addEventListener(
      'touchend',
      function () {
        start();
      },
      { passive: true }
    );

    start();
  }

  function initWelcomeCarousel(carousel) {
    if (carousel.getAttribute('data-dap-carousel-wired') === '1') return;
    carousel.setAttribute('data-dap-carousel-wired', '1');

    var viewport = carousel.querySelector('[data-dap-carousel-viewport]');
    var track = carousel.querySelector('[data-dap-carousel-track]');
    var slides = Array.prototype.slice.call(carousel.querySelectorAll('[data-dap-carousel-slide]'));
    var prev = carousel.querySelector('[data-dap-carousel-prev]');
    var next = carousel.querySelector('[data-dap-carousel-next]');
    var currentEl = carousel.querySelector('[data-dap-carousel-current]');
    var totalEl = carousel.querySelector('[data-dap-carousel-total]');
    if (!viewport || !track || slides.length < 2) return;

    var autoplay = carousel.getAttribute('data-autoplay') !== 'false';
    var interval = parseInt(carousel.getAttribute('data-interval'), 10) || 6000;
    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var index = 0;
    var timer = null;
    var scrollFrame = null;

    if (totalEl) totalEl.textContent = String(slides.length);

    function updateCurrent(nextIndex) {
      index = nextIndex;
      slides.forEach(function (slide, slideIndex) {
        if (slideIndex === index) {
          slide.setAttribute('data-current-slide', 'true');
        } else {
          slide.removeAttribute('data-current-slide');
        }
      });
      if (currentEl) currentEl.textContent = String(index + 1);
    }

    function slideLeft(slide) {
      return slide.offsetLeft - track.offsetLeft;
    }

    function goTo(nextIndex, smooth) {
      var targetIndex = (nextIndex + slides.length) % slides.length;
      updateCurrent(targetIndex);
      viewport.scrollTo({
        left: slideLeft(slides[targetIndex]),
        behavior: smooth && !reduceMotion ? 'smooth' : 'auto',
      });
    }

    function syncFromScroll() {
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(function () {
        var closestIndex = 0;
        var closestDistance = Infinity;
        slides.forEach(function (slide, slideIndex) {
          var distance = Math.abs(slideLeft(slide) - viewport.scrollLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = slideIndex;
          }
        });
        if (closestIndex !== index) updateCurrent(closestIndex);
      });
    }

    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      stop();
      if (!autoplay || reduceMotion || document.hidden) return;
      timer = window.setInterval(function () {
        goTo(index + 1, true);
      }, interval);
    }

    if (prev) {
      prev.addEventListener('click', function () {
        goTo(index - 1, true);
        start();
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        goTo(index + 1, true);
        start();
      });
    }

    viewport.addEventListener('scroll', syncFromScroll, { passive: true });
    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', start);
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', start);
    viewport.addEventListener('pointerdown', stop, { passive: true });
    viewport.addEventListener('pointerup', start, { passive: true });
    window.addEventListener('resize', function () {
      goTo(index, false);
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    updateCurrent(0);
    start();
  }

  function wireCouponCopy(root) {
    var buttons = root.querySelectorAll('[data-dap-coupon-copy]');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      if (btn.getAttribute('data-dap-copy-wired') === '1') return;
      btn.setAttribute('data-dap-copy-wired', '1');

      btn.addEventListener('click', function () {
        var coupon = btn.closest('[data-dap-coupon]');
        var codeEl = coupon && coupon.querySelector('[data-dap-coupon-code]');
        var code = codeEl ? (codeEl.textContent || '').trim() : '';
        if (!code) return;

        var labelCopy = btn.getAttribute('data-label-copy') || 'Copy';
        var labelCopied = btn.getAttribute('data-label-copied') || 'Copied!';

        function markCopied() {
          btn.textContent = labelCopied;
          btn.classList.add('is-copied');
          window.setTimeout(function () {
            btn.textContent = labelCopy;
            btn.classList.remove('is-copied');
          }, 1800);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(markCopied).catch(function () {
            fallbackCopy(code, markCopied);
          });
        } else {
          fallbackCopy(code, markCopied);
        }
      });
    });
  }

  function fallbackCopy(text, onDone) {
    var area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    try {
      document.execCommand('copy');
      if (onDone) onDone();
    } catch (err) {
      /* ignore */
    }
    document.body.removeChild(area);
  }

  function wireLeadForm(root) {
    var lead = root.querySelector('[data-dap-lead]');
    if (!lead) return;
    if (lead.getAttribute('data-dap-lead-wired') === '1') return;
    lead.setAttribute('data-dap-lead-wired', '1');

    wireCouponCopy(lead);

    var apiUrl = (lead.getAttribute('data-dap-lead-api') || '').trim();
    if (apiUrl) {
      wireLeadApiForm(lead, apiUrl);
      return;
    }

    var form = lead.querySelector('form');
    var tagsInput = lead.querySelector('[data-dap-lead-tags]');
    var noteInput = lead.querySelector('[data-dap-lead-note]');
    var channelSelect = lead.querySelector('[data-dap-lead-channel]');
    var discountInput = lead.querySelector('[data-dap-lead-discount-code]');
    if (!form || !tagsInput) return;

    var baseTags = (tagsInput.value || 'download-app-lead').trim();

    function syncMeta() {
      var tags = [baseTags];
      var notes = [];
      var search = new URLSearchParams(window.location.search);

      ['source', 'product', 'channel'].forEach(function (key) {
        var value = search.get(key);
        if (value) {
          tags.push(key + ':' + value.trim());
          notes.push(key + ': ' + value.trim());
        }
      });

      var selectedChannel = channelSelect && channelSelect.value ? channelSelect.value.trim() : '';
      if (selectedChannel) {
        tags.push('order-channel:' + selectedChannel.replace(/\s+/g, '-'));
        notes.push('Order channel: ' + selectedChannel);
      }

      var discountCode = discountInput && discountInput.value ? discountInput.value.trim() : '';
      if (discountCode) {
        tags.push('discount-offered');
        notes.push('Discount code offered: ' + discountCode);
      }

      tagsInput.value = tags.filter(Boolean).join(', ');
      if (noteInput) {
        noteInput.value = notes.join(' | ');
      }
    }

    if (channelSelect) {
      channelSelect.addEventListener('change', syncMeta);
    }
    form.addEventListener('submit', syncMeta);
    syncMeta();
  }

  function wireLeadApiForm(lead, apiUrl) {
    var form = lead.querySelector('[data-dap-lead-form]');
    var success = lead.querySelector('[data-dap-lead-success]');
    var errors = lead.querySelector('[data-dap-lead-errors]');
    var submitBtn = lead.querySelector('[data-dap-lead-submit]');
    var nameInput = lead.querySelector('[data-dap-lead-name]');
    var emailInput = lead.querySelector('[data-dap-lead-email]');
    var channelSelect = lead.querySelector('[data-dap-lead-channel]');
    var codeEl = lead.querySelector('[data-dap-coupon-code]');
    var coupon = lead.querySelector('[data-dap-coupon]');
    if (!form) return;

    var defaultError =
      lead.getAttribute('data-dap-lead-error') || 'Something went wrong. Please try again.';
    var baseTags = lead.getAttribute('data-dap-lead-tags-base') || 'download-app-lead';
    var idleLabel = submitBtn ? submitBtn.textContent : 'Submit';

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (errors) {
        errors.hidden = true;
        errors.textContent = '';
      }

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      var firstName = nameInput ? nameInput.value.trim() : '';
      var email = emailInput ? emailInput.value.trim() : '';
      var channel = channelSelect && channelSelect.value ? channelSelect.value.trim() : '';
      var search = new URLSearchParams(window.location.search);

      var payload = {
        firstName: firstName,
        email: email,
        channel: channel,
        tags: baseTags,
        source: search.get('source') || '',
        product: search.get('product') || '',
        trackChannel: search.get('channel') || '',
      };

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
        submitBtn.textContent = 'Please wait…';
      }

      fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, status: res.status, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok || !result.data || !result.data.ok || !result.data.code) {
            throw new Error(
              (result.data && result.data.error) || defaultError
            );
          }

          if (codeEl) codeEl.textContent = String(result.data.code).trim();
          if (coupon) coupon.hidden = false;
          form.hidden = true;
          if (success) {
            success.hidden = false;
            success.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        })
        .catch(function (err) {
          if (errors) {
            errors.hidden = false;
            errors.textContent = err && err.message ? err.message : defaultError;
          }
        })
        .then(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-loading');
            submitBtn.textContent = idleLabel;
          }
        });
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-dap-root]');
    if (!roots.length) return;

    var tracking = getTrackingParams();
    var platform = detectPlatform();

    roots.forEach(function (root) {
      wireLinks(root, tracking);
      wireDownload(root, platform, tracking);
      wireLeadForm(root);

      var banner = root.querySelector('[data-dap-banner]');
      if (banner) initBanner(banner);

      var welcomeCarousel = root.querySelector('[data-dap-welcome-carousel]');
      if (welcomeCarousel) initWelcomeCarousel(welcomeCarousel);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
